/**
 * You can modify this file
 *
 * @version 6
 *
 */
import Axios, { AxiosRequestConfig, AxiosError, AxiosResponse, AxiosInstance } from 'axios';
import { clearAccessToken, getAccessToken } from 'src/services/auth/tokenStore';
import { usePlanLimit } from 'src/states/planLimit';
import { planErrorFrom } from './planErrors';
import { recoverFromStaleTenantContext } from './tenantRecovery';

const baseConfig: AxiosRequestConfig = {
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
};

let axiosInstance: AxiosInstance;

function getAxiosInstance(): AxiosInstance {
  if (!axiosInstance) {
    axiosInstance = Axios.create(baseConfig);

    axiosInstance.interceptors.request.use(
      async (requestConfig) => {
        const token = await getAccessToken();
        if (requestConfig.headers && token) {
          requestConfig.headers.authorization = `Bearer ${token}`;
        }
        return requestConfig;
      },
      (error) => Promise.reject(error)
    );

    axiosInstance.interceptors.response.use(
      (async (response: AxiosResponse): Promise<SwaggerResponse<any>> => {
        if (import.meta.env.VITE_AXIOS_RESPONSE_DELAYED) {
          await new Promise((resolve) =>
            setTimeout(resolve, parseInt(import.meta.env.VITE_AXIOS_RESPONSE_DELAYED, 10))
          );
        }
        return response.data;
      }) as any,
      async (error: AxiosError) => {
        const original = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;

        // Plan limits are 402s, deliberately outside tenantRecovery's 403 family: a reload
        // cannot lift a plan limit, so it opens the upgrade modal instead.
        const planError = planErrorFrom(error);
        if (planError) {
          usePlanLimit.getState().show(planError);
          return Promise.reject(error);
        }

        // A 403 with a CONTEXT_MISMATCH-family code means the tenant cookie points
        // at a deleted restaurant/branch. Clear it and reload at `/`, where the root
        // ContextLoader re-resolves the default ladder and rewrites the cookie.
        if (recoverFromStaleTenantContext(error)) {
          return Promise.reject(error);
        }

        // A 401 usually means the 15-minute JWT aged out mid-session. Drop it, mint a
        // fresh one from the cookie session, and replay the request exactly once.
        if (error.response?.status === 401 && original && !original._retried) {
          original._retried = true;
          clearAccessToken();
          const token = await getAccessToken();
          if (token) {
            original.headers = { ...original.headers, authorization: `Bearer ${token}` };
            return axiosInstance.request(original);
          }
        }

        if (error.response?.status === 401) {
          clearAccessToken();
          const returnTo = encodeURIComponent(location.pathname + location.search);
          if (!location.pathname.startsWith('/sign-in')) {
            location.href = `/sign-in?returnTo=${returnTo}`;
          }
        }

        return Promise.reject(error);
      }
    );
  }
  return axiosInstance;
}

class RequestError extends Error {
  constructor(public message: string, public status?: number, public response?: AxiosResponse) {
    super(message);
  }
}

export type Security = any[] | undefined;

export type SwaggerResponse<R> = R;

export { getAxiosInstance, RequestError };
